import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { LAYER_NORM_CUSTOM } from '../../../../constants/layers';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { setHoveredLayer, setSelectedLayer } from '../../../../store/reducers/workspace.reducer.ts';
import { getNorms } from '../../../../store/selectors/user.selector';
import { selectWorkspaceMeasurementData } from '../../../../utils/measurementCalculation/measurementSource';
import {
  type ExpectedActualPair,
  getActualValueColor,
  getComparisonOperator,
  isTargetSatisfied,
} from '../../../../utils/normSatisfactionUtils.ts';
import { getNormName } from '../../../../utils/pdfUtils';
import { getPhotometricSymbol } from '../../../../utils/photometricSymbols.tsx';
import { getPhotometricUnit } from '../../../../utils/unitSystem.ts';
import { PanelSection } from '../PanelSection';

/** Per-layer results: norm, targets met and each metric against its required value. */
export function ResultsSection() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const norms = useSelector(getNorms);
  const measurementData = useAppSelector(selectWorkspaceMeasurementData);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const selectedLayer = useAppSelector((s) => s.workspace.selectedLayer);
  const hoveredLayer = useAppSelector((s) => s.workspace.hoveredLayer);

  const layers = useMemo(
    () =>
      (measurementData ?? [])
        .filter((layer) => layer.actualValues && layer.expectedValues.length > 0)
        .map((layer) => {
          const pairs: ExpectedActualPair[] = [];
          for (const expected of layer.expectedValues) {
            const actual = layer.actualValues!.find(
              (a) => a.function === expected.function && a.type === expected.type,
            );
            if (actual) {
              pairs.push({
                expected: expected.value,
                actual: actual.value,
                function: expected.function,
                type: expected.type,
              });
            }
          }
          const match =
            modelLayers.find((l) => l.threeJsName === layer.name) ??
            modelLayers.find((l) => l.originalName === layer.name);
          return {
            layer,
            pairs,
            satisfied: pairs.filter((pair) => isTargetSatisfied(pair)).length,
            displayName: match?.originalName || layer.name,
            normName: layer.normId
              ? getNormName(layer.normId, norms)
              : match?.selectedNorm === LAYER_NORM_CUSTOM
                ? t('workspace.customTargets.chip')
                : '',
          };
        }),
    [measurementData, modelLayers, norms, t],
  );

  const satisfied = layers.reduce((sum, l) => sum + l.satisfied, 0);
  const total = layers.reduce((sum, l) => sum + l.pairs.length, 0);

  return (
    <PanelSection
      title={t('workspace.results')}
      count={total > 0 ? t('workspace.panel.results.targets', { satisfied, total }) : undefined}
    >
      {layers.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-gray-400">{t('workspace.panel.results.empty')}</p>
      ) : (
        <div className="p-3 space-y-2">
          {layers.map(({ layer, pairs, satisfied: met, displayName, normName }) => (
            <div
              key={layer.uuid}
              onClick={() => dispatch(setSelectedLayer(layer.name === selectedLayer ? null : layer.name))}
              onMouseEnter={() => dispatch(setHoveredLayer(layer.name))}
              onMouseLeave={() => dispatch(setHoveredLayer(null))}
              className={`rounded-lg overflow-hidden border cursor-pointer transition-colors ${
                selectedLayer === layer.name
                  ? 'border-lime-400 ring-2 ring-lime-400/80'
                  : hoveredLayer === layer.name
                    ? 'border-lime-400/50'
                    : 'border-white/10'
              }`}
              style={{ background: 'linear-gradient(170deg, rgba(79, 209, 46, 0.15) 0%, rgba(35, 35, 35, 0.95) 100%)' }}
            >
              <div className="flex items-baseline gap-2 px-3 pt-2 pb-1 min-w-0">
                <span className="flex-1 min-w-0 truncate text-[13px] font-medium text-white" title={displayName}>
                  {displayName}
                </span>
                <span className="shrink-0 text-[11px] tabular-nums">
                  <span
                    className={met === pairs.length ? 'text-lime-400 font-semibold' : 'text-amber-300 font-semibold'}
                  >
                    {met}
                  </span>
                  <span className="text-gray-400">/{pairs.length}</span>
                </span>
              </div>
              {normName && (
                <p className="px-3 -mt-0.5 pb-1 text-[11px] text-gray-400 truncate" title={normName}>
                  {t('workspace.norm')}: {normName}
                </p>
              )}
              <div className="px-3 pb-2">
                <div className="grid grid-cols-12 text-[9px] uppercase tracking-wide text-gray-400 mb-0.5">
                  <div className="col-span-4" />
                  <div className="col-span-4">{t('workspace.value', 'Value')}</div>
                  <div className="col-span-4">{t('workspace.targetValue', 'Required')}</div>
                </div>
                {pairs.map((pair) => {
                  const uom = getPhotometricUnit(pair.function);
                  const ok = isTargetSatisfied(pair);
                  return (
                    <div
                      key={`${pair.function}-${pair.type}`}
                      className="grid grid-cols-12 items-center h-5 text-[11px] border-b border-white/5 last:border-0"
                    >
                      <div className="col-span-4 flex items-center gap-1.5 text-gray-300">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-lime-400' : 'bg-red-400'}`}
                          title={ok ? t('workspace.targetMet') : t('workspace.targetNotMet')}
                        />
                        {getPhotometricSymbol(pair.function, pair.type)}
                      </div>
                      <div className={`col-span-4 font-medium tabular-nums ${getActualValueColor(pair)}`}>
                        {pair.actual?.toFixed(2)}
                        {uom ? ` ${uom}` : ''}
                      </div>
                      <div className="col-span-4 text-gray-300 tabular-nums">
                        {getComparisonOperator(pair.type)} {pair.expected?.toFixed(2)}
                        {uom ? ` ${uom}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </PanelSection>
  );
}
